import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlusCircle, Trash2, Loader2, ListChecks } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const CamaFormModal = ({ isOpen, onClose, onSave, editingItem, prendasParametrizadas = [], loadingPrendas = false }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    capacidad: 1,
    prendas: [],
    estado: 'Activo'
  });

  const [selectedPrendaId, setSelectedPrendaId] = useState('');
  const [prendaDescripcionExtra, setPrendaDescripcionExtra] = useState('');
  const [prendaCantidad, setPrendaCantidad] = useState(1);
  // Cantidad por defecto al usar "Agregar todas"
  const [addAllCantidad, setAddAllCantidad] = useState(1);

  useEffect(() => {
    if (editingItem) {
      setFormData({
        nombre: editingItem.nombre || '',
        descripcion: editingItem.descripcion || '',
        capacidad: editingItem.capacidad || 1,
        prendas: editingItem.prendas || [],
        estado: editingItem.estado || 'Activo'
      });
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        capacidad: 1,
        prendas: [],
        estado: 'Activo'
      });
    }
    setSelectedPrendaId('');
    setPrendaDescripcionExtra('');
    setPrendaCantidad(1);
    setAddAllCantidad(1);
  }, [editingItem, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddPrenda = () => {
    if (!selectedPrendaId || prendaCantidad < 1) {
      return;
    }

    const prendaSeleccionada = prendasParametrizadas.find(p => p.id === selectedPrendaId);
    if (!prendaSeleccionada) {
      return;
    }

    // Verificar si la prenda ya está agregada
    const yaExiste = formData.prendas.some(p => p.nombre === prendaSeleccionada.nombre);
    if (yaExiste) {
      return;
    }

    const nuevaPrenda = {
      nombre: prendaSeleccionada.nombre,
      peso: prendaSeleccionada.peso,
      cantidad: parseInt(prendaCantidad),
      descripcion: prendaDescripcionExtra || prendaSeleccionada.descripcion
    };

    setFormData(prev => ({
      ...prev,
      prendas: [...prev.prendas, nuevaPrenda]
    }));

    setSelectedPrendaId('');
    setPrendaDescripcionExtra('');
    setPrendaCantidad(1);
  };

  const handleRemovePrenda = (index) => {
    setFormData(prev => ({
      ...prev,
      prendas: prev.prendas.filter((_, i) => i !== index)
    }));
  };

  // Agrega todas las prendas parametrizadas que aún no estén en la lista,
  // usando la cantidad definida en addAllCantidad. No reemplaza las existentes.
  const handleAddAllPrendas = () => {
    const cantidad = parseInt(addAllCantidad) || 1;
    if (cantidad < 1) return;

    setFormData(prev => {
      const nombresActuales = new Set(prev.prendas.map(p => p.nombre));
      const nuevas = prendasParametrizadas
        .filter(p => !nombresActuales.has(p.nombre))
        .map(p => ({
          nombre: p.nombre,
          peso: p.peso,
          cantidad,
          descripcion: p.descripcion || ''
        }));

      if (nuevas.length === 0) return prev;

      return {
        ...prev,
        prendas: [...prev.prendas, ...nuevas]
      };
    });
  };

  const handleUpdateCantidadPrenda = (index, nuevaCantidad) => {
    setFormData(prev => ({
      ...prev,
      prendas: prev.prendas.map((prenda, i) => 
        i === index 
          ? { ...prenda, cantidad: parseInt(nuevaCantidad) || 1 }
          : prenda
      )
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.nombre || formData.capacidad < 1) {
      return;
    }

    onSave(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingItem ? 'Editar Cama' : 'Nueva Cama'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información básica */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => handleChange('nombre', e.target.value)}
                placeholder="Ej: Cama Individual"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="capacidad">Capacidad (personas) *</Label>
              <Input
                id="capacidad"
                type="number"
                min="1"
                value={formData.capacidad}
                onChange={(e) => handleChange('capacidad', parseInt(e.target.value))}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción</Label>
            <Textarea
              id="descripcion"
              value={formData.descripcion}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              placeholder="Descripción de la cama"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="estado">Estado</Label>
            <Select value={formData.estado} onValueChange={(value) => handleChange('estado', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Activo">Activo</SelectItem>
                <SelectItem value="Inactivo">Inactivo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sección de prendas */}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Label className="text-lg font-semibold">Prendas Asociadas</Label>
                <span className="text-sm text-gray-500">
                  {formData.prendas.length} prenda(s)
                </span>
              </div>

              {/* Selector rápido: agregar todas las disponibles con una cantidad común */}
              {!loadingPrendas && prendasParametrizadas.length > 0 && (
                <div className="flex items-center gap-2">
                  <Label htmlFor="add-all-cantidad" className="text-xs text-gray-600 whitespace-nowrap">
                    Cant. para todas:
                  </Label>
                  <Input
                    id="add-all-cantidad"
                    type="number"
                    min="1"
                    value={addAllCantidad}
                    onChange={(e) => setAddAllCantidad(e.target.value)}
                    className="w-16 h-8 text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddAllPrendas}
                    disabled={
                      parseInt(addAllCantidad) < 1 ||
                      formData.prendas.length >= prendasParametrizadas.length
                    }
                    className="gap-1"
                    title={
                      formData.prendas.length >= prendasParametrizadas.length
                        ? 'Todas las prendas ya están agregadas'
                        : 'Agrega todas las prendas disponibles que aún no estén en la lista'
                    }
                  >
                    <ListChecks className="h-4 w-4" />
                    {formData.prendas.length >= prendasParametrizadas.length
                      ? 'Todas agregadas'
                      : 'Agregar todas'}
                  </Button>
                </div>
              )}
            </div>

            {/* Formulario para agregar prenda */}
            <Card className="bg-gray-50">
              <CardContent className="pt-6">
                {loadingPrendas ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-sm text-gray-600">Cargando prendas...</span>
                  </div>
                ) : prendasParametrizadas.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500">
                    No hay prendas parametrizadas disponibles. 
                    <br />
                    Por favor, crea tipos de prenda primero.
                  </div>
                ) : (
                  <div className="grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-5 space-y-2">
                      <Label htmlFor="prenda-select" className="text-sm">Seleccionar Prenda</Label>
                      <Select value={selectedPrendaId} onValueChange={setSelectedPrendaId}>
                        <SelectTrigger id="prenda-select">
                          <SelectValue placeholder="Selecciona una prenda..." />
                        </SelectTrigger>
                        <SelectContent>
                          {prendasParametrizadas.map((prenda) => (
                            <SelectItem key={prenda.id} value={prenda.id}>
                              {prenda.nombre} ({prenda.peso} g)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="prenda-cantidad" className="text-sm">Cantidad</Label>
                      <Input
                        id="prenda-cantidad"
                        type="number"
                        min="1"
                        value={prendaCantidad}
                        onChange={(e) => setPrendaCantidad(e.target.value)}
                        placeholder="1"
                      />
                    </div>

                    <div className="col-span-4 space-y-2">
                      <Label htmlFor="prenda-descripcion-extra" className="text-sm">Descripción adicional</Label>
                      <Input
                        id="prenda-descripcion-extra"
                        value={prendaDescripcionExtra}
                        onChange={(e) => setPrendaDescripcionExtra(e.target.value)}
                        placeholder="Opcional"
                      />
                    </div>

                    <div className="col-span-1">
                      <Button
                        type="button"
                        onClick={handleAddPrenda}
                        size="sm"
                        className="w-full"
                        disabled={!selectedPrendaId || prendaCantidad < 1}
                      >
                        <PlusCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lista de prendas agregadas */}
            {formData.prendas.length > 0 && (
              <div className="space-y-2">
                {formData.prendas.map((prenda, index) => (
                  <Card key={index} className="border-l-4 border-l-blue-500">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{prenda.nombre}</p>
                          <p className="text-xs text-gray-500">{prenda.descripcion || '-'}</p>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="text-sm text-gray-600">
                            <span className="font-semibold">{prenda.peso} g</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-gray-600">Cant:</Label>
                            <Input
                              type="number"
                              min="1"
                              value={prenda.cantidad || 1}
                              onChange={(e) => handleUpdateCantidadPrenda(index, e.target.value)}
                              className="w-16 h-8 text-sm"
                            />
                          </div>
                          
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemovePrenda(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit">
              {editingItem ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CamaFormModal;
